import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { StoryCardPostContentBlockFields } from '../../../../../../services';
import { ImageUploadType, UploadType, useUpload } from '../../../../../hooks';
import { BlockTypes, ImageBlockData } from '../../../../../models';
import { bytesToMegabytes } from '../../../../../utils';
import { docxToContentBlocks } from './docxToContentBlocks';

const DOCX_FILE_SIZE_LIMIT_MB = 20;
const TOTAL_IMAGE_UPLOAD_LIMIT = 20;

export const useDocxImport = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'importDocx' });

  const { uploadHandler } = useUpload();

  const [isImporting, setIsImporting] = useState(false);

  const importDocx = useCallback(
    async (file: File): Promise<StoryCardPostContentBlockFields[] | null> => {
      if (!file.name.toLowerCase().endsWith('.docx')) {
        toast.error(ctxT('format-error'));
        return null;
      }

      if (bytesToMegabytes(file.size) > DOCX_FILE_SIZE_LIMIT_MB) {
        toast.error(t('fileUpload.file-too-large-error'));
        return null;
      }

      setIsImporting(true);

      const toastId = toast.loading(ctxT('loading'));

      try {
        const { contentBlocks, imageFiles, imageBlockIndexes, skippedMediaCount } =
          await docxToContentBlocks(file);

        if (!contentBlocks.length) {
          toast.error(ctxT('empty-error'));
          return null;
        }

        const imagesToUpload = imageFiles.slice(0, TOTAL_IMAGE_UPLOAD_LIMIT);

        let failedUploadsCount = 0;

        for (const [fileIndex, imageFile] of imagesToUpload.entries()) {
          const block = contentBlocks[imageBlockIndexes[fileIndex]];

          if (!block || block.type !== BlockTypes.image) {
            failedUploadsCount += 1;
            continue;
          }

          toast.update(toastId, {
            render: ctxT('uploading-images', {
              current: fileIndex + 1,
              total: imagesToUpload.length,
            }),
          });

          const { id } = await uploadHandler({
            file: imageFile,
            type: UploadType.IMAGE,
            imageUploadType: ImageUploadType.ITEM,
          });

          if (!id) {
            failedUploadsCount += 1;
            continue;
          }

          (block.data as ImageBlockData).items.push({
            image: { id, url: URL.createObjectURL(imageFile) },
            caption: '',
          });
        }

        const filteredContentBlocks = contentBlocks.filter(
          (block) => block.type !== BlockTypes.image || (block.data as ImageBlockData).items.length
        );

        if (failedUploadsCount) {
          toast(ctxT('images-skipped', { total: failedUploadsCount }));
        }

        if (imageFiles.length > TOTAL_IMAGE_UPLOAD_LIMIT) {
          toast(ctxT('images-limit', { limit: TOTAL_IMAGE_UPLOAD_LIMIT }));
        }

        if (skippedMediaCount) {
          toast(ctxT('media-skipped'));
        }

        return filteredContentBlocks;
      } catch (_) {
        toast.error(ctxT('error'));
        return null;
      } finally {
        toast.dismiss(toastId);
        setIsImporting(false);
      }
    },
    [ctxT, t, uploadHandler]
  );

  return { importDocx, isImporting };
};
